import React from 'react';
import { Grid, Typography } from '@mui/material';
import { Cancel } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { AccountData, CanceledOrderData, OrderData } from '../../../../api';
import { getOrderPage } from '../../../../utils/AccountUtils';
import { useLanguage } from '../../../../common/GeneralUtils';
import TimeUtils from '../../../../utils/TimeUtils';
import { useAuth } from '../../../../session/InternalAuthProvider';
import { getDateFormat, getTimeFormat } from '../../../../common/getDateAndTimeFormat';

type Props = {
  canceledOrder: CanceledOrderData;
  orderDetail?: OrderData;
};

export const CanceledOrderRow = ({ canceledOrder, orderDetail }: Props) => {
  const { t } = useTranslation();
  const language = useLanguage();
  const { authState } = useAuth();
  const account = authState.account as unknown as AccountData;
  const dateFormat = getDateFormat(account).longDateFormat;
  const timeFormat = getTimeFormat(account);
  const orderPage = orderDetail ? getOrderPage(account, orderDetail.order_page_id) : null;
  const pageName = orderPage?.label?.[language] || orderPage?.label?.en || '';
  const canceledDateLabel = orderDetail?.date ? moment(orderDetail.date).format(dateFormat) : '';
  const canceledTimeLabel = orderDetail?.time != null ? moment.unix(orderDetail.time).tz('utc').format(timeFormat) : '';
  const canceledDateTimeLabel = [canceledDateLabel, canceledTimeLabel].filter(Boolean).join(' ');
  const orderLabel = orderDetail
    ? `#${orderDetail.order_number}${pageName ? ` - ${pageName}` : ''}${
        canceledDateTimeLabel ? ` - ${canceledDateTimeLabel}` : ''
      }`
    : canceledOrder.order_id;
  const canceledAt = TimeUtils.getLocalTime(canceledOrder.canceled_at, account.timezone).format(dateFormat);
  const canceledByName = canceledOrder.canceled_by_user_id
    ? (account.users ?? []).find((u) => u.user_id === canceledOrder.canceled_by_user_id)
    : undefined;

  return (
    <Grid container flexDirection="column" py={0.5} sx={{ opacity: 0.7 }}>
      <Grid container alignItems="center" flexWrap="nowrap">
        <Cancel fontSize="small" sx={{ marginInlineEnd: 1, color: 'text.secondary', flexShrink: 0 }} />
        <Typography variant="body2" sx={{ textDecoration: 'line-through' }}>
          {orderLabel}
        </Typography>
      </Grid>
      <Typography variant="caption" color="text.secondary" sx={{ marginInlineStart: 3 }}>
        {t('passes.canceled_at', { date: canceledAt })}
        {canceledOrder.cancel_reason && ` · ${t(`passes.cancel_reason.${canceledOrder.cancel_reason}`)}`}
        {canceledOrder.canceled_by_user_id &&
          ` · ${
            canceledByName
              ? t('passes.canceled_by', { name: `${canceledByName.first_name} ${canceledByName.last_name}` })
              : t('passes.canceled_by_unknown')
          }`}
      </Typography>
    </Grid>
  );
};
